import { useMemo, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { toast } from 'react-toastify'
import posthog from 'posthog-js'
import Header from 'components/headers/easyPay'
import DotsLoading from 'components/loadings/dotsLoading'
import { SectionError } from 'components/states'
import ManualPaymentCard from 'components/manualPayment'
import { faPlain, money, toman } from 'utilities/helper/format'
import {
    BUCKET_LABELS,
    PreselectMode,
    recordModeFunds,
} from 'services/easyPay/payables'
import usePayables from './hooks/usePayables'
import PayableRow from './payableRow'

/**
 * «پرداختنی‌ها» — the select-and-pay screen.
 *
 * Four things changed from the flat list this replaces:
 *
 *  1. Grouped by bucket, each group with its own subtotal and select-all.
 *  2. Overdue items arrive ticked; everything else does not. The old screen
 *     ticked *everything*, so a member with 900k overdue and 600k not yet due
 *     opened pre-committed to 1.5M they never chose.
 *  3. Record-mode funds are listed but not payable, with the manager's number.
 *     The backend used to drop them entirely, so a member of such a fund
 *     opened this screen and saw nothing at all.
 *  4. The basket is reconciled against the server before the user is sent to a
 *     gateway — see `reconcile` in usePayables for why that matters.
 *
 * The entry point decides what arrives ticked:
 *   ?filter=overdue  (also the bare default)  — اقساط سررسید شده
 *   ?filter=dues     — حق عضویت و پرداختنی‌های پیش‌رو
 *   ?select=all      — everything
 *
 * and `?box=<id>&box_type=<csb|mnp>` narrows the screen to a single box, which
 * is how the صندوق قرعه‌کشی detail page sends a member here to settle just that
 * box's share rather than everything they owe everywhere. `box_type` defaults
 * to `csb` because that is the only entry point that passes `box` today.
 */
export default function EasyGatewayPay() {
    const navigate = useNavigate()
    const [params] = useSearchParams()

    const preselect: PreselectMode = useMemo(() => {
        if (params.get('select') === 'all') return 'all'
        return params.get('filter') === 'dues' ? 'dues' : 'overdue'
    }, [params])

    const boxId = useMemo(() => {
        const raw = params.get('box')
        const parsed = Number(raw)
        return raw && Number.isFinite(parsed) ? parsed : undefined
    }, [params])
    const boxType = params.get('box_type') ?? (boxId ? 'csb' : undefined)

    const {
        buckets,
        totals,
        payables,
        isLoading,
        isError,
        toggle,
        toggleBucket,
        toggleAll,
        reconcile,
        refetch,
    } = usePayables({ preselect, boxId, boxType })

    const [checking, setChecking] = useState(false)

    const recordFunds = useMemo(() => recordModeFunds(payables), [payables])
    const selectableCount = payables.filter((p) => p.bucket !== 'record').length
    const everythingSelected =
        selectableCount > 0 && totals.count === selectableCount

    const onPay = async () => {
        if (totals.count === 0) return
        setChecking(true)
        try {
            const moved = await reconcile()
            if (moved) {
                toast.info(
                    'بعضی از موارد سبد شما تغییر کرده بود؛ سبد به‌روزرسانی شد. لطفاً دوباره تایید کنید.'
                )
                return
            }
            posthog.capture('payables_pay_started', {
                selected_count: totals.count,
                total_count: selectableCount,
                had_overdue: payables.some(
                    (p) => p.isSelected && p.bucket === 'overdue'
                ),
                total: totals.amount + totals.commission,
            })
            navigate('/easy-pay/init-new', {
                state: {
                    total: totals.amount,
                    commission: totals.commission,
                    toBePaidTransactions: payables.filter((p) => p.isSelected),
                },
            })
        } finally {
            setChecking(false)
        }
    }

    if (isLoading) return <DotsLoading />

    const hasPayableGroups = buckets.some((b) => b.bucket !== 'record')

    return (
        <div className="flex flex-col h-screen bg-canvas">
            <div className="grow-0">
                <Header title="پرداختنی‌ها" />
            </div>

            <div className="grow overflow-y-auto px-3 pt-3 pb-4">
                {isError ? (
                    <SectionError onRetry={() => refetch()} />
                ) : buckets.length === 0 ? (
                    <p className="text-center text-sm text-text-secondary mt-10">
                        فعلاً پرداختنی ندارید
                    </p>
                ) : (
                    <>
                        {selectableCount > 0 && (
                            <label className="flex items-center gap-2 px-1 pb-2 cursor-pointer">
                                <input
                                    type="checkbox"
                                    checked={everythingSelected}
                                    onChange={toggleAll}
                                    className="w-4 h-4 accent-green-1"
                                />
                                <span className="text-sm text-text-body">
                                    همه را انتخاب کن
                                </span>
                            </label>
                        )}

                        {buckets.map((group) => (
                            <div key={group.bucket} className="mb-4">
                                <div className="flex items-center justify-between gap-2 px-1 mb-2">
                                    <span className="flex items-center gap-2">
                                        {group.bucket !== 'record' && (
                                            <input
                                                type="checkbox"
                                                checked={group.allSelected}
                                                onChange={() =>
                                                    toggleBucket(group.bucket)
                                                }
                                                aria-label={`انتخاب همه — ${
                                                    BUCKET_LABELS[group.bucket]
                                                }`}
                                                className="w-4 h-4 accent-green-1 cursor-pointer"
                                            />
                                        )}
                                        <span className="text-xs font-bold text-text-secondary">
                                            {BUCKET_LABELS[group.bucket]}
                                        </span>
                                    </span>
                                    <span className="text-xs font-bold text-text-secondary">
                                        {money(group.total)} تومان
                                    </span>
                                </div>

                                <div className="flex flex-col gap-2">
                                    {group.items.map((item) => (
                                        <PayableRow
                                            key={item.key}
                                            item={item}
                                            onToggle={toggle}
                                        />
                                    ))}
                                </div>

                                {/* One card per fund, not per row: the card
                                    number and the manager are properties of
                                    the fund, and repeating them under each of
                                    five installments reads as five different
                                    instructions. */}
                                {group.bucket === 'record' &&
                                    recordFunds.map((fund) => (
                                        <div key={fund.boxKey} className="mt-2">
                                            <ManualPaymentCard
                                                info={fund.info}
                                                boxName={fund.boxName}
                                                amount={fund.total}
                                                source="payables"
                                            />
                                        </div>
                                    ))}
                            </div>
                        ))}
                    </>
                )}
            </div>

            {hasPayableGroups && (
                <div className="grow-0 bg-white border-t border-divider px-4 py-3">
                    {/* Label first, value second: under direction:rtl the first
                        flex child sits on the RIGHT, which is where a Persian
                        reader expects the label and not the number. */}
                    <div className="flex justify-between text-xs text-text-secondary">
                        <span>هزینه‌ی اشتراک</span>
                        <span>{money(totals.commission)} تومان</span>
                    </div>
                    <div className="flex justify-between text-base font-bold text-heading mt-1">
                        <span>مبلغ کل</span>
                        <span>{toman(totals.amount + totals.commission)}</span>
                    </div>
                    <button
                        type="button"
                        disabled={totals.count === 0 || checking}
                        onClick={onPay}
                        className="mt-3 w-full rounded-full py-2 text-sm font-bold text-white bg-green-1 disabled:bg-text-muted disabled:cursor-not-allowed cursor-pointer"
                    >
                        {totals.count === 0
                            ? 'موردی انتخاب نشده است'
                            : checking
                            ? 'در حال بررسی…'
                            : `پرداخت ${faPlain.format(totals.count)} مورد`}
                    </button>
                    <button
                        type="button"
                        onClick={() => navigate(-1)}
                        className="w-full text-center text-text-secondary text-sm mt-2 cursor-pointer"
                    >
                        بعداً
                    </button>
                </div>
            )}
        </div>
    )
}
